import React, { useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "@/components/common";
import { TextInput, Textarea, Select } from "@/components/DataTable";

function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid={testid}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(value === o ? "" : o)}
          className={`px-4 py-1.5 text-sm transition-colors ${value === o ? "bg-seq-green text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{o}</button>
      ))}
    </div>
  );
}

export function FormField({ field, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const set = (v) => onChange(field.id, v);
  const label = <>{field.label}{field.required && <span className="text-red-500"> *</span>}</>;
  const testid = `form-field-${field.id}`;

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const cur = Array.isArray(value) ? [...value] : [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/files/upload", fd);
        cur.push({ id: data.id, url: data.url, name: data.filename });
      }
      set(cur);
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  if (field.type === "long_text")
    return <Field label={label}><Textarea rows={3} value={value || ""} onChange={(e) => set(e.target.value)} data-testid={testid} />{field.help && <p className="text-xs text-slate-400 mt-1">{field.help}</p>}</Field>;
  if (field.type === "number")
    return <Field label={label}><TextInput type="number" value={value || ""} onChange={(e) => set(e.target.value)} data-testid={testid} /></Field>;
  if (field.type === "date")
    return <Field label={label}><TextInput type="date" value={value || ""} onChange={(e) => set(e.target.value)} data-testid={testid} /></Field>;
  if (field.type === "dropdown")
    return <Field label={label}><Select value={value || ""} onChange={(e) => set(e.target.value)} data-testid={testid}><option value="">Select…</option>{(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</Select></Field>;
  if (field.type === "yesno")
    return <Field label={label}><Segmented options={["Yes", "No"]} value={value || ""} onChange={set} testid={testid} /></Field>;
  if (field.type === "passfail")
    return <Field label={label}><Segmented options={["Pass", "Fail"]} value={value || ""} onChange={set} testid={testid} /></Field>;
  if (field.type === "checkbox")
    return <label className="flex items-center gap-2 text-sm text-slate-600 py-1" data-testid={testid}><input type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} className="rounded" /> {field.label}{field.required && <span className="text-red-500"> *</span>}</label>;
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <Field label={label}>
        <div className="space-y-1" data-testid={testid}>
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={arr.includes(o)} onChange={() => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])} className="rounded" /> {o}</label>
          ))}
        </div>
      </Field>
    );
  }
  if (field.type === "photo") {
    const arr = Array.isArray(value) ? value : [];
    const backend = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("seq_token") || "";
    return (
      <Field label={label}>
        <div className="flex flex-wrap gap-2" data-testid={testid}>
          {arr.map((p, i) => (
            <div key={i} className="relative h-16 w-16 rounded-lg overflow-hidden border border-slate-200">
              <img src={`${backend}${p.url}?auth=${token}`} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => set(arr.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"><X size={11} /></button>
            </div>
          ))}
          <label className="h-16 w-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer text-slate-400 hover:border-seq-green">
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            <input type="file" accept="image/*" multiple className="hidden" onChange={onPhoto} disabled={uploading} />
          </label>
        </div>
      </Field>
    );
  }
  return <Field label={label}><TextInput value={value || ""} onChange={(e) => set(e.target.value)} data-testid={testid} />{field.help && <p className="text-xs text-slate-400 mt-1">{field.help}</p>}</Field>;
}
